import React, { FC, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';

import { Button } from 'devextreme-react/button';
import { Template } from 'devextreme-react/core/template';
import DataGrid, {
  Column,
  ColumnChooser,
  Export,
  FilterPanel,
  FilterRow,
  GroupPanel,
  Lookup,
  Pager,
  Paging,
  StateStoring,
} from 'devextreme-react/data-grid';
import 'devextreme-react/text-area';

import { MONEY_FORMAT } from 'constants/formats';
import { useAuth } from 'context/auth';
import { onExporting } from 'utils/exportingExcel';
import { useConfirmDialog } from 'utils/hooks';

import { useApproveComingNomenclatureMutation } from 'bus/comingNomenclatureMain/services/main';
import { ComingNomenclatureMain } from 'bus/comingNomenclatureMain/types';

import { AppCard, AppLayoutTop } from '../../../../components/appComponents';
import ClaimsProvider from '../../../../components/claimsBlock';
import $createStore from '../../../../utils/create-store-instance';
import { DeleteModal, ReportModal } from './components';

const ComingNomenclature: FC = () => {
  const { getClaims }: any = useAuth();

  const canLookPrice = getClaims(['CanLookPrice']);
  const history = useHistory();

  const comingNomenclatureDataStore: any = useMemo(() => {
    return $createStore('/v1/comingNomenclature/', 'id');
  }, []);

  const statusesStore: any = useMemo(() => {
    return $createStore('/v1/ComingNomenclature/statuses', 'id');
  }, []);

  const { getConfirmation } = useConfirmDialog();

  const [approveComingNomenclature] = useApproveComingNomenclatureMutation();

  const refGrid = useRef<DataGrid | null>(null);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [selectComingNomenclature, setSelectComingNomenclature] = useState<ComingNomenclatureMain | null>(null);
  const [isVisiblereportModal, seIsVisiblereportModal] = useState(false);

  const refreshGrid = () => refGrid.current?.instance.refresh();

  const openReportModal = () => seIsVisiblereportModal(true);
  const closeReportModal = () => seIsVisiblereportModal(false);

  const handleOpenConfirmDialog = async () => {
    const isConfirmed = await getConfirmation({
      title: 'Массовое утверждение приходов',
      message: 'Вы хотите утвердить все приходы?',
      textConfirmButton: 'Утвердить',
    });

    const filtering = refGrid.current?.instance.getCombinedFilter();

    if (isConfirmed) {
      await approveComingNomenclature(JSON.stringify(filtering));
      refreshGrid();
    }
  };

  return (
    <div className="container-dev">
      <AppLayoutTop title="Приход материалов">
        <ClaimsProvider claimsBlock={['CanCreateComingMaterial']}>
          <Button width={120} text="Создать" type="default" onClick={() => history.push('/comming/add')} />
        </ClaimsProvider>
      </AppLayoutTop>

      <ReportModal onClose={closeReportModal} visible={isVisiblereportModal} />

      <DeleteModal
        changeVisible={(visible) => {
          setVisibleDeleteModal(visible);
        }}
        id={selectComingNomenclature?.id!}
        refresh={refreshGrid}
        visible={visibleDeleteModal}
      />

      <AppCard>
        <DataGrid
          dataSource={comingNomenclatureDataStore}
          showBorders={false}
          loadPanel={{
            enabled: true,
            text: 'Загрузка',
          }}
          key="id"
          ref={refGrid}
          onExporting={onExporting}
          allowColumnResizing
          columnResizingMode="nextColumn"
          columnMinWidth={50}
          allowColumnReordering
          onToolbarPreparing={(e) => {
            e.toolbarOptions?.items?.unshift({
              location: 'after',
              template: 'tempButton',
            });
            e.toolbarOptions?.items?.unshift({
              location: 'after',
              template: 'confirmButton',
            });
          }}
          remoteOperations
          onRowDblClick={(e) => history.push(`/comming-materials/${e.data.id}`)}
        >
          <Template name="tempButton">
            <Button icon="download" onClick={openReportModal} />
          </Template>
          <Template name="confirmButton">
            <Button icon="check" onClick={handleOpenConfirmDialog} />
          </Template>
          <StateStoring enabled={true} type="localStorage" storageKey="comingNomenclature" />
          <FilterRow visible={true} />
          <GroupPanel visible={true} />
          <FilterPanel visible={true} />
          <ColumnChooser enabled={true} />
          <Column dataField="id" caption="Номер" />
          <Column dataField="status" caption="Статус" width={70}>
            <Lookup dataSource={statusesStore} valueExpr="id" displayExpr="name" />
          </Column>
          <Column dataField="date" caption="Дата" dataType="date" sortOrder="desc" width={150} />
          <Column dataField="contractor" caption="Поставщик" />
          <Column dataField="address" caption="Карьер" />
          <Column dataField="nomenclature" caption="Материал" />
          <Column dataField="nomenclatureAnotation" caption="Материал по учёту" />
          <Column dataField="factoryName" caption="Склад" />
          <Column dataField="comment" caption="Комментарий" />
          <Column dataField="quantity" caption="Кол-во" width={70} />
          <Column dataField="unitName" caption="Ед. изм." width={70} />
          <Column dataField="price" caption="Цена" width={70} />
          <Column
            dataField="calc"
            caption="Сумма"
            format={MONEY_FORMAT}
            width={120}
            visible={canLookPrice}
            cellRender={(data) => <span>{(data.data.price * data.data.quantity).toLocaleString()}</span>}
          />
          <Column
            dataField="actions"
            caption=""
            width={120}
            cellRender={(data) => (
              <>
                <Button
                  icon="edit"
                  type="default"
                  stylingMode="text"
                  onClick={() => history.push(`/comming-materials/${data.data.id}`)}
                />
                <ClaimsProvider claimsBlock={['CanDeleteConsumptionNomenclature']}>
                  <Button
                    icon="trash"
                    type="default"
                    stylingMode="text"
                    onClick={() => {
                      setVisibleDeleteModal(true);
                      setSelectComingNomenclature(data.data);
                    }}
                  />
                </ClaimsProvider>
              </>
            )}
          />
          <Export enabled={true} />
          <Paging defaultPageSize={15} />
          <Pager showPageSizeSelector={true} allowedPageSizes={[15, 30, 50, 100]} />
        </DataGrid>
      </AppCard>
    </div>
  );
};

export default ComingNomenclature;
